<script setup>
    // Store
    import { useNewsDetailStore } from '@/stores/modal/newsDetailStore';
    import { useHomeStore } from '@/stores/home/homeStore';

    // Vue
    import { computed, defineEmits, defineExpose, inject } from 'vue';
    import { ref } from 'vue';
    import Pagination from '@/components/PaginationComponentNewsDetail.vue';
    import utils from '@/commonProject/helpers/utils';
    const homeStore = useHomeStore();
    const api = ref('');
    // ----------------------------
    // お知らせ詳細
    // ----------------------------
    const newsDetailStore = useNewsDetailStore();
    const newsModal = ref({
        newsModalDetail: 'block',
        newsModalComplete: '',
    });

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');
    
    // 本文に含まれるhttp、httpsをリンクに変換する。
    // 既存aタグはそのまま維持し、aタグ外のURLのみ自動リンク化する（2重リンク防止）。
    const noticeMessageHtml = computed(() => {
        const text = newsDetailStore.NoticeMessage || '';
        const withLinks = text.replace(/(<a\b[^>]*>[\s\S]*?<\/a>)|(https?:\/\/[^\s<>"]+)/gi, (match, anchor, url) => {
            if (anchor) return anchor;
            return `<a href="${url}">${url}</a>`;
        });
        return withLinks.replace(/\r\n|\n|\r/g, '<br>');
    });

    /**
     * ページ操作
     * @param newPage
     */
    // ページングパーツ
    const currentPage = ref(1);
    const totalPages = ref(0);
    async function handlePageChange(newPage) {
        const page = newPage - 1;
        currentPage.value = newPage;
        await getNewsDetail(homeStore.List[page].NoticeBulletinPk, homeStore.List[page].NoticeReaded);
    }
    const pagination = ref();
    /**
     * ページングの初期処理を実行する。
     * 取得したAPIのデータ数を引き渡す。
     */
    function handleOnloadPagination(RowResult) {
        pagination.value.onload(RowResult);
    }

    /**
     * お知らせ詳細取得処理
     * @param requestdForm
     */
    async function getNewsDetail(noticeBulletinPk, noticeReaded) {
        // Storeを初期化する。
        newsDetailStore.NoticeDt = '';
        newsDetailStore.NoticeSubject = '';
        newsDetailStore.NoticeMessage = '';

        newsModal.value.newsModalDetail = 'block';
        newsModal.value.newsModalComplete = '';

        const param = {
            NoticeBulletinPk: noticeBulletinPk,
        };

        const response = await newsDetailStore.getNewsDetailAction(api.value, param);

        if (response.success) {
            if (!noticeReaded) {
                const requestBody2 = {
                    NoticeBulletinPk: noticeBulletinPk,
                };
                const response2 = await newsDetailStore.updateNewsDetailReadedAction(api.value, requestBody2);

                if (!response2.success) {
                    currentPage.value = 1;
                    totalPages.value = 0;

                    messageDialog('error', response2.errorMessage);
                    if (response2.redirect) {
                        location.href = response2.redirectUrl;
                    }
                    return;
                }
            }
            if (!utils.isNullEmptyArray(homeStore.List)) {
                // お知らせモーダルのページング制御のために、最大ページ数を取得する。
                handleOnloadPagination(homeStore.List.length);
            }
        } else {
            currentPage.value = 1;
            totalPages.value = 0;

            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    // ----------------------------
    // 親側実装必須処理
    // ----------------------------
    const emit = defineEmits(['newsDetailModal:close', 'newsDetailModal:openContactModalFromNewDetailModal']);

    function close() {
        document.removeEventListener('click', function () {}, true);
        // 親画面のモーダル非表示処理を呼び出す
        emit('newsDetailModal:close');
    }

    // ----------------------------
    // 親画面から利用可能関数
    // ----------------------------
    function newsDetailSet(API_URL, noticeBulletinPk, noticeReaded, index) {
        currentPage.value = index + 1;
        totalPages.value = homeStore.List.length;
        api.value = API_URL;
        getNewsDetail(noticeBulletinPk, noticeReaded);

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalInformation .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    }

    defineExpose({
        newsDetailSet,
    });
</script>
<style scoped>
    .informationTitleTitle {
        flex: 1 1 auto;
        min-width: 0;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
    }

    .informationTitleDate {
        flex: 0 0 auto;
        margin-left: auto;
        white-space: nowrap;
    }
    
    .informationBodyLink {
        height: calc(100% - 64px);
        margin-bottom: 10px;
        width: 100%;
        display: block;
        background: #e5e8ea;
        border: 1px solid #6b6b6b;
        padding: 8px;
        border-radius: 4px;
        box-sizing: border-box;
        font-weight: 400;
        white-space: pre-wrap;
        word-break: break-all;
        overflow-y: auto;
    }
</style>
<template>
    <article id="modalInformation" class="modal" :style="{ display: newsModal.newsModalDetail }">
        <h3 class="textCenter">{{ $t('newsDetail.parts.modalTtitle1') }}</h3>
        <section class="modalContents" style="height: calc(100% - 145px)">
            <ul class="informationTitle">
                <li class="informationTitleTitle">
                    <span>{{ newsDetailStore.NoticeSubject }}</span>
                </li>
                <li class="informationTitleDate">
                    <span>{{ newsDetailStore.NoticeDt }}</span>
                </li>
            </ul>
            <div class="informationBody informationBodyLink" v-html="noticeMessageHtml" />
            <Pagination ref="pagination" :currentPage="currentPage" :totalPages="totalPages" @pageChanged="handlePageChange" />
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal" @click="close">{{ $t('common.parts.close') }}</button>
                </li>
            </ul>
        </section>
    </article>
</template>
